/*
  Dawn origin. Joard changes (2026-10-20):
  - open(): the focus-trap container falls back to #CartDrawer when the drawer is flagged empty but the
    re-rendered markup has no .drawer__inner-empty (the add-to-cart on the product page re-renders through
    renderContents without clearing is-empty, which made trapFocus(null) throw); focus is also trapped after a
    short timeout when no transitionend fires (prefers-reduced-motion disables the transition).
  - renderContents(): syncs the is-empty class from the cart state and announces the addition in the
    #CartDrawer-Announce live region once the drawer is visible.
  - announcePending() (2026-11-02): the announcement also reads the free-shipping progress line, whose own
    live region is recreated by the re-render and so never announced.
*/
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    let trapped = false;
    const trap = () => {
      if (trapped) return;
      trapped = true;
      clearTimeout(fallback);
      const emptyContainer = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : null;
      const containerToTrapFocusOn = emptyContainer || document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      this.announcePending();
    };
    this.addEventListener('transitionend', trap, { once: true });
    const fallback = setTimeout(trap, 450);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  announcePending() {
    if (!this.pendingAnnouncement) return;
    const region = this.querySelector('#CartDrawer-Announce');
    const progress = this.querySelector('.joard-cart__progress-label');
    const text = [this.pendingAnnouncement, progress ? progress.textContent.trim() : ''].filter(Boolean).join(' ');
    this.pendingAnnouncement = null;
    if (!region) return;
    region.textContent = '';
    setTimeout(() => {
      region.textContent = text;
    }, 50);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // An addition always leaves the cart non-empty; the rendered markup is the source of truth.
    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));

    const added = parsedState.product_title || (parsedState.items && parsedState.items[0] && parsedState.items[0].product_title);
    if (added && this.dataset.addedText) {
      this.pendingAnnouncement = this.dataset.addedText.replace('[product]', added);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
